import { gql }                                                                                                                                                  from 'apollo-boost';
import { ACTIVITY_STATUS, projectBusinessFunctionFields, projectDescFields, projectMilestoneFields, projectVersionDetailFields, projectVersionMilestoneFields } from '@src/constants';
import { getFieldWithProjectIdAccessor, getFieldWithProjectVersionIdAccessor, getProjectVersionsWhereIdAccessor }                                               from "@src/util/network/QueryUtility";
import { SystemQueries }                                                                                                                                        from './SystemQueries';
import { Uuid }                                                                                                                                                 from "@src/types";


const _projectDescFields             = projectDescFields.join( ' ' ),
      _projectVersionDetailFields    = projectVersionDetailFields.join( ' ' ),
      _projectVersionMilestoneFields = projectVersionMilestoneFields.join( ' ' ),
      _projectBusinessFunctionFields = projectBusinessFunctionFields.join( ' ' ),
      _projectMilestoneFields        = projectMilestoneFields.join( ' ' );

export const fragments = {

    PROJECTS: {
        QUERY_RETURN:                `id name isActive`,
        MUTATION_RETURN:             `id name isActive projectVersions { ${ _projectVersionDetailFields } } isDiscoveryComplete`,
        CONFIG_FIELDS:               `
            id 
            name 
            isActive 
            isTemplateSource 
            isDiscoveryComplete 
            termDictionary 
            projectVersions { 
                ${ _projectVersionDetailFields } 
            } 
            businessFunctions {  
                ${ _projectBusinessFunctionFields }
                contextOwner
                contextOwnerProject { id name }
                contextOwnerOrganization {id name}
            }
            milestones { 
                ${ _projectMilestoneFields }
                contextOwner
                contextOwnerProject { id name }
                contextOwnerOrganization {id name} 
            }
        `,
        EDIT_FIELDS:                 ( projectVersionId = null ) => `
			id
			name
			${ _projectDescFields }
			organizationId
			assetId
			asset{
			    organizationId
			}
			projectMembers{
				accountId
				projectRole
			}
			isActive
			isTemplateSource
			termDictionary
			revenue
		`,
        ASSUMPTIONS_FIELDS:          `
			${ _projectDescFields }
			organizationId
		`,
        DETAIL_FIELDS:               `id name`,
        ASSET_DESC_FIELDS:           _projectDescFields,
        VERSION_DETAIL_FIELDS:       _projectVersionDetailFields,
        TIMELINE_BOUNDARY_FIELDS:    ( projectVersionId = null ) => ( `
            ${ getFieldWithProjectVersionIdAccessor( projectVersionId, 'mapLowerBound' ) }
            ${ getFieldWithProjectVersionIdAccessor( projectVersionId, 'mapUpperBound' ) }
            `
        ),
        MILESTONE_DEFINITION_FIELDS: _projectMilestoneFields,
        MILESTONE_FIELDS:            _projectVersionMilestoneFields,
        DEFINED_TIMING_FIELDS:       () => ( `
            milestones { 
                ${ _projectMilestoneFields } 
            }
            projectVersions {
                id 
                projectVersionMilestones{
                    ${ _projectVersionMilestoneFields }
                }
            }`
        )
    },

    PROJECT_VERSIONS: {
        QUERY_RETURN:    `id name description`,
        MUTATION_RETURN: `id projectId name description`,
        EDIT_FIELDS:     `id projectId name description isActiveVersion`
    },

    PROJECT_ANNUAL_OBJECTIVES: {
        MUTATION_RETURN: `projectId year description`
    },

    PROJECT_EXPORT_DATA_REPORT: {
        MUTATION_RETURN: `id projectId reportFormatType reportUrl createdUtc createdBy{ firstName lastName } targetDataSets`
    },

    PROJECT_HIRES: {
        QUERY_RETURN: ( projectId?: string, projectVersionId = null ) => `
			id
			hires {
				id
				headcount
				salary
				modifier
				hireDate				
				projectVersionMilestoneId
                projectVersionMilestoneOffsetMonths					
				hireType {
					${ getFieldWithProjectIdAccessor( projectId, 'name' ) }
					resourceCode
					businessFunction { id }
				}
				staticProjectActivity {
                        id
                        name
                        businessFunction { id }
                        activity {
                            id
                        }
                    }
                }
			}`
    },

    PROJECT_HIRE_TYPES: {
        QUERY_RETURN: ( projectVersionId?: string ) => `
			id
			${ getProjectVersionsWhereIdAccessor( projectVersionId ) } {
			    hireTypes {
			        id
                    name
                    description
                    hireTypeId
                    salary
                    modifier
                    note
                    businessFunctionId
                    businessFunction {
                        id
                        name
                        resourceCode
                        colorValue
                    }
                    hires {
                        id
                        headcount
                        isActive
                        hireDate
                        note
                        projectVersionMilestoneId
                        projectVersionMilestoneOffsetMonths
                        staticProjectActivityId					
                        staticProjectActivity {
                            id
                            name
                            subObjectiveId
                            businessFunctionId
                            businessFunction { id name resourceCode colorValue }
                            activity {
                                id
                                hires {
                                    hireTypeId
                                }
                            }
                            requiredHires{
                                headcount
                                hireTypeId
                            } 
                        }
                    }
                    hireType {
                        id
                        name
                        description
                        resourceCode
                        businessFunctionId
                        businessFunction { id name resourceCode colorValue }
                        managingHireType {
                            name
                        }
                    }
			    }
			}`
    },

    PROJECT_HIRE_TYPE: {
        MUTATION_RETURN: ( projectId?: string, projectVersionId = null ) => `
            hireTypeId
            salary
            modifier
            recommendedSalary
            recommendedModifier
            businessFunction { id }
            hires {
                id
                hireDate
                headcount
                isActive
                note
                staticProjectActivityId
                staticProjectActivity {
                    id
                    name
                    businessFunction { id }
                    activity { 
                        id 
                    }
                }
            }
            hireType{
                id
                name
                resourceCode
                businessFunction { id }
            }`
    },

    PROJECT_FEES: {
        QUERY_RETURN: `id professionalFees { id feeDate amount professionalFee { name resourceCode } projectActivity{ id activity{ displayName businessFunction { id } } } }`
    },

    PROJECT_WORKFLOWS: {
        QUERY_RETURN: `
			id
			projectWorkFlowTasks {
				id
				assignedAccount { id firstName lastName businessFunctions { id } }
				updatedBy{ id firstName lastName businessFunctions }
				updatedUtc
				completedUtc
				workflowActionStatus
				workflowTask {
					id
					name
					ordinal
					description
					workflowActionType
					workflowContextType
				}
				${ SystemQueries.fragments.AUDIT_RECORDS.QUERY_RETURN }
			}`,

        TASK_RETURN: `
			id
			assignedAccount { id firstName lastName businessFunctions }
			updatedBy{ id firstName lastName businessFunctions }
			updatedUtc
			completedUtc
			workflowActionStatus
			workflowTask {
				id
				name
				ordinal
				description
				workflowActionType
				workflowContextType
			}
			${ SystemQueries.fragments.AUDIT_RECORDS.QUERY_RETURN }`,

        TASK_DEFINITION_RETURN: `
			id
			name
			ordinal
			description
			workflowActionType
			workflowContextType
		`
    },

    PROJECT_TEMPLATES: {
        QUERY_RETURN: `
            id
			category
            description
            icon
            isArchived
            name
            sourceProjectId
            sourceProjectVersionId
            project{ id organizationId projectMembers { accountId projectRole } }
            organizations { organizationId }
            milestones { id name milestoneDate }
            tags { tag }`,

        MUTATION_RETURN: `
            id
			category
            description
            icon
            isArchived
            name
            organizations { organizationId }
            milestones { id name milestoneDate }
            tags { tag }`
    },

    PROJECT_BUDGET_FEE_ITEMS: {
        QUERY_RETURN: ( projectId?: Uuid, projectVersionId = null ) => `
        
                    staticBudget (
                        includeRecurrenceActivities: true,
                        projectVersionId: "${ projectVersionId }",
                        completionStateIn: [
                            ${ ACTIVITY_STATUS.NOTSTARTED },
                            ${ ACTIVITY_STATUS.INCOMPLETE },
                            ${ ACTIVITY_STATUS.COMPLETE },
                        ]
                    ){
                        
						feeBasedCostOccurrences{
							costOccurrence
							costAmount
							costAmountRange{ min max }
							costAmountType
							instanceType
							itemData{
								__typename
								
								... on StaticProjectActivity{		
															
									id
									name
									businessFunction { id }
						            
						            startDate
						            endDate
						            
						            subObjectiveId
						            subObjectiveName
									
									requiredHires {
									    headcount
									    hireTypeId
									    hireType{
                                            id
                                            name
                                            resourceCode
                                            businessFunction { id }
                                        }
									}
						            
						            operationalCost
									operationalCostNote
									operationalCostReplacesProfessionalFees
																		
									ongoingCostQuarters
									ongoingCostPerQuarter
									ongoingCostNote									
									
									scheduledBudgetItems{
                                        id
                                        amount
                                        name
                                        note
                                        year
                                        quarter
									}
									
									professionalFees{
										id
										name
										amount
										resourceCode
										professionalFeeId
									}
									
									activity{ 
									    id
									}
								}
								
								... on StaticProjectBudgetItem {
									id 
									name 
									categoryId 
									subCategoryName 
									businessFunction { id } 
									values{ 
									    id 
									    value 
									    startDate 
                                    }
								}	
                            }
						}
					}
		      `
    },

    PROJECT_BUDGET_RESOURCING_ITEMS: {
        QUERY_RETURN: ( projectVersionId = null ) => `
        
            staticBudget( projectVersionId:"${ projectVersionId }", includeRecurrenceActivities: true ){
                       
                resourceCostOccurrences{
                    hireTypeId
                    hireTypeBusinessFunctionId
                    hireTypeBusinessFunction { id name colorValue resourceCode }
                    resourceCostOccurrence
                    isInitialHireOccurrence
                    quarterlyCompensation
                    staticProjectHire {
                        id
                        note
                        hireDate
                        headcount
                        salary
                        modifier
                        hireType {
                                id
                                name
                                description
                                resourceCode
                                businessFunction { id name colorValue iconName resourceCode }
                                managingHireType{
                                    name
                                }
                            }
                            staticProjectActivity {
                                 id
                                 name
                                 businessFunction { id name colorValue iconName resourceCode }
                                 categoryId
                                 categoryName
                                 subCategoryName
                                 subCategoryId
                                 requiredHires{
                                     headcount
                                     hireTypeId
                                     hireType {
                                         id
                                         name
                                         resourceCode
                                         businessFunction { id name colorValue iconName resourceCode }
                                     }
                                 }
                                 activity { 
                                     id 
                                 }
                            }
                    }
                }
            }`
    }
};

export const queries = {

    /* PROJECTS
     * --------------------------------------------------------------------------------------------*/
    GET_PROJECTS: ( returnQueryString = fragments.PROJECTS.QUERY_RETURN ) => {

        const query = `
            query GetProjects( $order: [ProjectSortInput!], $where: ProjectFilterInput ){
                projects( order: $order, where: $where)  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    GET_PROJECT: ( returnQueryString = fragments.PROJECTS.QUERY_RETURN ) => {

        const query = `
            query GetProject( $id: Uuid! ){
                projects( where: { id: { eq: $id } } )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    GET_PROJECT_HIRES: ( returnQueryString = fragments.PROJECT_HIRES.QUERY_RETURN() ) => {

        const query = `
            query GetProjectHires( $id: Uuid! ){
                projects( where: { id: { eq: $id } } )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    GET_PROJECT_HIRE_TYPES: ( returnQueryString = fragments.PROJECT_HIRE_TYPES.QUERY_RETURN() ) => {

        const query = `
            query GetProjectHireTypes( $projectId: Uuid! ){
                projects( where: { id: { eq: $projectId } } )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    GET_PROJECT_WORKFLOWS: ( returnQueryString = fragments.PROJECT_WORKFLOWS.QUERY_RETURN ) => {

        const query = `
            query GetProjectWorkflows( $id: Uuid! ){
                projects( where: { id: { eq: $id } } )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    GET_PROJECT_TEMPLATES: ( returnQueryString = fragments.PROJECT_TEMPLATES.QUERY_RETURN ) => {

        const query = `
            query GetProjectTemplates {
                me {
                    id
                    projectTemplates {
                        ${ returnQueryString }
                    }
                }
            }`;

        return gql( query );
    }
};

export const mutations = {

    /* PROJECTS
     * --------------------------------------------------------------------------------------------*/
    CREATE_PROJECT: ( returnQueryString = fragments.PROJECTS.MUTATION_RETURN ) => {

        const query = `
            mutation CreateProject( $input: ProjectCreateCommandInput! ){
                createProject( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    CREATE_PROJECT_FROM_TEMPLATE: ( returnQueryString = fragments.PROJECTS.MUTATION_RETURN ) => {

        const query = `
            mutation CreateProjectFromTemplate( $input: ProjectCreateFromTemplateCommandInput! ){
                createProjectFromTemplate( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    CREATE_PROJECT_TEMPLATE: ( returnQueryString = fragments.PROJECT_TEMPLATES.MUTATION_RETURN ) => {

        const query = `
            mutation CreateProjectTemplate( $input: ProjectTemplateCreateCommandInput! ){
                createProjectTemplate( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    UPDATE_PROJECT_TEMPLATE: ( returnQueryString = fragments.PROJECT_TEMPLATES.MUTATION_RETURN ) => {

        const query = `
            mutation UpdateProjectTemplate( $input: ProjectTemplateUpdateCommandInput! ){
                updateProjectTemplate( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    CREATE_PROJECT_VERSION: ( returnQueryString = fragments.PROJECT_VERSIONS.MUTATION_RETURN ) => {

        const query = `
            mutation CreateProjectVersion($input: ProjectVersionCreateCommandInput!) {
                createProjectVersion(input: $input) {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    UPDATE_PROJECT_VERSION: ( returnQueryString = fragments.PROJECT_VERSIONS.MUTATION_RETURN ) => {

        const query = `
            mutation UpdateProjectVersion($input: ProjectVersionUpdateCommandInput!) {
                updateProjectVersion(input: $input) {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    DELETE_PROJECT_VERSION: () => {

        const query = `
            mutation DeleteProjectVersion($input: ProjectVersionDeleteCommandInput!) {
                deleteProjectVersion(input: $input)
            }`;

        return gql( query );
    },


    UPDATE_PROJECT: ( returnQueryString = fragments.PROJECTS.MUTATION_RETURN ) => {

        const query = `
            mutation UpdateProject( $input: ProjectUpdateCommandInput! ){
                updateProject( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    DUPLICATE_PROJECT: ( returnQueryString = fragments.PROJECTS.MUTATION_RETURN ) => {

        const query = `
            mutation DuplicateProject( $input: ProjectCloneCommandInput! ) {
                cloneProject( input: $input ) {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    CREATE_PROJECT_ANNUAL_OBJECTIVE: ( returnQueryString = fragments.PROJECT_ANNUAL_OBJECTIVES.MUTATION_RETURN ) => {

        const query = `
            mutation CreateProjectAnnualObjective( $input: ProjectAnnualObjectiveCreateCommandInput! ){
                createProjectAnnualObjective( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    UPDATE_PROJECT_ANNUAL_OBJECTIVE: ( returnQueryString = fragments.PROJECT_ANNUAL_OBJECTIVES.MUTATION_RETURN ) => {

        const query = `
            mutation UpdateProjectAnnualObjective( $input: ProjectAnnualObjectiveUpdateCommandInput! ){
                updateProjectAnnualObjective( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    UPDATE_PROJECT_HIRE_TYPE: ( returnQueryString = fragments.PROJECT_HIRE_TYPE.MUTATION_RETURN(), projectVersionId = null ) => {

        const query = `
            mutation UpdateProjectHireType( $input:ProjectVersionHireTypeUpdateCommandInput! ){ 
                updateProjectVersionHireType( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    UPDATE_PROJECT_BUDGET_ITEMS: ( returnQueryString = fragments.PROJECT_HIRE_TYPE.MUTATION_RETURN(), projectVersionId = null ) => {

        const query = `
            mutation SaveProjectBudgetItems( $input: StaticProjectBudgetItemSaveCommandInput! ){
                saveStaticProjectBudgetItems( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    UPDATE_PROJECT_WORKFLOW_TASK: ( returnQueryString = fragments.PROJECT_WORKFLOWS.TASK_RETURN ) => {

        const query = `
            mutation UpdateProjectWorkflowTask( $input: ProjectWorkflowTaskUpdateActionStatusCommandInput! ){
                updateProjectWorkflowTaskActionStatus( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    REASSIGN_PROJECT_WORKFLOW_TASK: ( returnQueryString = fragments.PROJECT_WORKFLOWS.TASK_RETURN ) => {

        const query = `
            mutation ReassignProjectWorkflowTask( $input: ProjectWorkflowTaskReassignCommandInput! ){
                reassignProjectWorkflowTask( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    EXPORT_PROJECT_DATA: ( returnQueryString = fragments.PROJECT_EXPORT_DATA_REPORT.MUTATION_RETURN ) => {

        const query = `
            mutation ExportProjectDataToFile( $input: ReportCreateCommandInput! ){
                createReport( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    UPDATE_PROJECT_VERSION_DATA: () => {

        const query = `
            mutation UpdateProjectVersionData( $input: ProjectVersionUpdateDataCommandInput! ){
                updateProjectVersionData( input: $input ){
                    success
                    invalidActivityIds
                    invalidDependencyIds
                    invalidEventIds
                }
            }`;

        return gql( query );
    },
};

export const ProjectQueries = {
    fragments,
    queries,
    mutations,
};
